import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import type { StatefulDiaryT } from "@/types/database/diaries";
import dayjs from "dayjs";
import { useMemo } from "react";
import { Bar, BarChart, Tooltip, XAxis } from "recharts";
import { TrendBadge } from "./TrendBadge";

interface WeeklyDataCardProps {
	diaries: StatefulDiaryT[];
}

export function WeeklyDataCard({ diaries }: WeeklyDataCardProps) {
	const totalEntries = diaries.reduce((sum, diary) => {
		return (
			sum +
			diary.trackers_view.reduce((trackerSum, tracker) => {
				return (
					trackerSum +
					(tracker.entries_view?.reduce((entrySum, entry) => {
						return entrySum + (entry.entry_values?.length || 0);
					}, 0) || 0)
				);
			}, 0)
		);
	}, 0);

	// Prepare data for the questions answered chart
	const questionsAnsweredData = useMemo(() => {
		if (!diaries.length) return [];

		// Create a map of dates to entry counts
		const dateMap = new Map<string, number>();

		for (const diary of diaries) {
			for (const tracker of diary.trackers_view) {
				for (const entry of tracker.entries_view || []) {
					const date = dayjs(entry.calculated_entry_time).format("YYYY-MM-DD");
					const entryCount = entry.entry_values?.length || 0;

					if (entryCount > 0) {
						dateMap.set(date, (dateMap.get(date) || 0) + entryCount);
					}
				}
			}
		}

		// Convert map to array of objects for the chart
		return Array.from(dateMap.entries())
			.map(([date, value]) => ({
				date,
				value,
			}))
			.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
	}, [diaries]);

	// Calculate weekly average for questions answered
	const weeklyAverage = useMemo(() => {
		if (!questionsAnsweredData.length) return 0;

		const totalQuestions = questionsAnsweredData.reduce(
			(sum, item) => sum + item.value,
			0,
		);
		const lastDate =
			questionsAnsweredData[questionsAnsweredData.length - 1]?.date;
		const firstDate = questionsAnsweredData[0]?.date;

		if (!lastDate || !firstDate) return totalQuestions;

		const weeks = Math.ceil(
			dayjs(lastDate).diff(dayjs(firstDate), "week", true),
		);

		return weeks > 0 ? Math.round(totalQuestions / weeks) : totalQuestions;
	}, [questionsAnsweredData]);

	// Calculate trend for questions answered
	const questionsTrend = useMemo(() => {
		if (questionsAnsweredData.length < 2) return 0;

		const firstHalf = questionsAnsweredData.slice(
			0,
			Math.floor(questionsAnsweredData.length / 2),
		);
		const secondHalf = questionsAnsweredData.slice(
			Math.floor(questionsAnsweredData.length / 2),
		);

		const firstHalfAvg =
			firstHalf.reduce((sum, item) => sum + item.value, 0) / firstHalf.length;
		const secondHalfAvg =
			secondHalf.reduce((sum, item) => sum + item.value, 0) / secondHalf.length;

		return secondHalfAvg - firstHalfAvg;
	}, [questionsAnsweredData]);

	return (
		<Card className="w-full md:w-1/3">
			<CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
				<CardTitle className="font-medium text-sm">
					Questions Answered
				</CardTitle>
				<div className="flex items-center gap-1 text-xs">
					<TrendBadge value={Math.round(questionsTrend)} />
				</div>
			</CardHeader>
			<CardContent>
				<div className="flex flex-col gap-1">
					<div className="flex justify-between items-baseline">
						<div className="font-bold text-2xl">{totalEntries}</div>
						<div className="flex items-center gap-1 text-xs">
							<span className="text-muted-foreground">Weekly avg:</span>
							<span className="font-medium">{weeklyAverage}</span>
						</div>
					</div>
					{questionsAnsweredData.length > 0 && (
						<div className="mt-2 h-[120px] overflow-hidden">
							<ChartContainer
								className="w-full h-[120px]"
								config={{
									questions: {
										label: "Questions",
										color: "hsl(var(--chart-2))",
									},
								}}
							>
								<BarChart data={questionsAnsweredData} height={100}>
									<XAxis
										dataKey="date"
										tickLine={false}
										tickMargin={10}
										axisLine={{ stroke: "#e2e8f0", strokeWidth: 1 }}
										tick={{ fill: "#64748b", fontSize: 12 }}
										height={30}
										tickFormatter={(value) => dayjs(value).format("MMM D")}
									/>
									<Tooltip
										content={({ active, payload, label }) => {
											if (active && payload && payload.length) {
												return (
													<div className="bg-white/80 shadow-sm backdrop-blur-sm p-2 border border-slate-200 rounded-md">
														<div className="flex justify-between items-center gap-2">
															<div className="flex items-center gap-1.5">
																<div className="bg-[hsl(var(--chart-2))] rounded-full w-2.5 h-2.5" />
																<span className="font-medium text-slate-700 text-xs">
																	Questions
																</span>
															</div>
															<span className="font-medium tabular-nums text-slate-900 text-xs">
																{payload[0]?.value || 0}
															</span>
														</div>
													</div>
												);
											}
											return null;
										}}
									/>
									<Bar
										dataKey="value"
										fill="hsl(var(--chart-2))"
										radius={[2, 2, 0, 0]}
									/>
								</BarChart>
							</ChartContainer>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
